/**
 * The document (web plan W4.3, React Router framework mode): what index.html
 * and main.tsx set up, rendered by the server for every page.
 *
 * - `loader`: the SEO server's W1.3 decision for the URL (@/seo/documentHead):
 *   redirects and the 503 are thrown, 200 / 404 / 410 come back with their
 *   status and head, and the URL they were decided for. It runs for the
 *   document only (`shouldRevalidate`).
 * - `meta`: that head (the tags server/head.mjs injected) while the location
 *   is that URL (`servesDocumentHead`); a route module's own `meta` (W4.4 on)
 *   replaces it whole. Nothing anywhere else (after an in-app navigation,
 *   without the SEO server): the page renders its own head there
 *   (@/components/seo/PageHead, web plan W5.1). Nothing either when the
 *   loader failed: the ErrorBoundary writes its own `<title>` and `noindex`.
 * - `Layout`: index.html's static head (icons, manifest, theme colours, the
 *   pre-paint theme script, GA4), the JSON-LD (on the served URL) and the
 *   `<noscript>` copy in the body.
 * - The default component: the providers main.tsx put around `<App/>`.
 *
 * Every head tag is React's: the document's from `meta`, a page's own from
 * `<PageHead/>`, never both at once, and nothing rewrites either in place.
 */
import { useContext, useState, type ReactNode } from 'react';
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  UNSAFE_LocationContext,
  useRouteError,
  useRouteLoaderData,
  type HeadersFunction,
  type LinksFunction,
  type LoaderFunctionArgs,
  type MetaFunction,
  type ShouldRevalidateFunction,
} from 'react-router';
import stylesheet from '@/index.css?url';
import interLatin from '@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url';
import { AppProviders } from '@/AppProviders';
import { PersistedCacheRestore } from '@/components/PersistedCacheRestore';
import { RenderTimeContext } from '@/hooks/useRenderNow';
import { tFor } from '@/i18n';
import { documentApolloClient } from '@/lib/documentApolloClient';
import {
  headMeta,
  JSONLD_ELEMENT_ID,
  jsonLdHtml,
  loadDocument,
  noscriptCopy,
  servesDocumentHead,
  type DocumentData,
} from '@/seo/documentHead';
import { DEFAULT_LOCALE, htmlAttributes } from '@/seo/locales';
import { splitLocalePath } from '@/seo/urls';
import { SITE_NAME } from '@/seo/templates';

export function loader({ context, request }: LoaderFunctionArgs) {
  return loadDocument(context, request);
}

/** The document's decision is made once; later navigations never ask the server again. */
export const shouldRevalidate: ShouldRevalidateFunction = () => false;

/** The loader's Cache-Control (and a 503's Retry-After), W1.3's. */
export const headers: HeadersFunction = ({ loaderHeaders, errorHeaders }) => errorHeaders ?? loaderHeaders;

export const meta: MetaFunction = ({ data, location }) => {
  const loaded = data as DocumentData | undefined;
  return servesDocumentHead(loaded, location) ? headMeta(loaded.head, { error: loaded.error }) : [];
};

/**
 * The stylesheet, and the Latin subset of the variable Inter (48 KB) preloaded: it is the one file
 * every page's text needs, and without the hint the browser finds it only after the CSS is parsed
 * (audit F19). The other unicode-range subsets stay lazy (they are fetched only for those scripts).
 * `crossOrigin` is required for a font preload even from the same origin.
 */
export const links: LinksFunction = () => [
  { rel: 'preload', as: 'font', type: 'font/woff2', href: interLatin, crossOrigin: 'anonymous' },
  { rel: 'stylesheet', href: stylesheet },
];

/**
 * GA4 (gtag.js), as index.html had it: never from local development hosts;
 * `send_page_view: false` because page views are reported per navigation
 * (@/lib/analytics, <PageViews/> in @/App).
 */
const GA_SNIPPET = `(function () {
  var id = 'G-R6T2Y2397W';
  var host = location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    dataLayer.push(arguments);
  };
  gtag('js', new Date());
  gtag('config', id, { send_page_view: false });
  var s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.googletagmanager.com/gtag/js?id=' + id;
  document.head.appendChild(s);
})();`;

/** The theme before first paint (no flash); ThemeContext takes over after hydration. */
const THEME_SNIPPET = `(function () {
  try {
    var t = localStorage.getItem('theme');
    var dark = t === 'dark' || (t !== 'light' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    if (dark) document.documentElement.classList.add('dark');
  } catch (e) {}
})();`;

/** The two origins every page calls first (index.html's preconnects), from the build's env. */
const API_ORIGIN: string | undefined = import.meta.env.VITE_BASE_URL || undefined;
const MEDIA_ORIGIN: string | undefined = import.meta.env.VITE_MEDIA_URL || undefined;

export function Layout({ children }: { children: ReactNode }) {
  const loaded = useRouteLoaderData('root') as DocumentData | undefined;
  const head = loaded?.head ?? null;
  // The document's JSON-LD belongs to its URL: after an in-app navigation the
  // page renders its own (`<PageHead/>`, same id). No router: the document's.
  const location = useContext(UNSAFE_LocationContext)?.location ?? null;
  const jsonLd = servesDocumentHead(loaded, location) ? jsonLdHtml(head) : null;
  // The page's own locale (the URL's prefix, else the default one's): its words, links and <html lang>.
  const locale = (location && splitLocalePath(location.pathname)?.locale) || DEFAULT_LOCALE;
  const copy = noscriptCopy(head, { locale });
  return (
    // The theme script may add `dark` to <html> before hydration.
    <html lang={head?.htmlLang ?? locale.hreflang} dir={head ? head.htmlDir : htmlAttributes(locale).dir} suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />
        <Meta />
        <meta name="application-name" content={SITE_NAME} />
        <meta name="author" content={SITE_NAME} />
        <meta name="google-site-verification" content="bmH-KKUeqy7s3bJqvCp6YxyO_WiPJpnJyvBSxWaEwEs" />
        <meta name="color-scheme" content="light dark" />
        <meta name="theme-color" content="#FAF9F6" media="(prefers-color-scheme: light)" />
        <meta name="theme-color" content="#111827" media="(prefers-color-scheme: dark)" />
        <meta name="format-detection" content="telephone=no" />
        <link rel="icon" href="/favicon.ico" sizes="any" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="icon" type="image/png" sizes="192x192" href="/icon-192.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="manifest" href="/site.webmanifest" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content={SITE_NAME} />
        {API_ORIGIN ? <link rel="preconnect" href={API_ORIGIN} crossOrigin="anonymous" /> : null}
        {MEDIA_ORIGIN ? <link rel="preconnect" href={MEDIA_ORIGIN} /> : null}
        <Links />
        <script dangerouslySetInnerHTML={{ __html: GA_SNIPPET }} />
        <script dangerouslySetInnerHTML={{ __html: THEME_SNIPPET }} />
      </head>
      <body>
        {/* JSON-LD in the body (web plan W4.3). */}
        {jsonLd ? (
          <script id={JSONLD_ELEMENT_ID} type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
        ) : null}
        <div id="root">{children}</div>
        <noscript>
          <div
            style={{
              maxWidth: 640,
              margin: '64px auto',
              padding: '0 24px',
              fontFamily: 'system-ui, sans-serif',
              textAlign: 'center',
              color: '#1a1a1a',
            }}
          >
            {/* Not an <h1>: the page's one H1 is the app's own (seo-smoke counts the body's). */}
            <p style={{ fontSize: 28, fontWeight: 700, margin: '21px 0' }}>{copy.h1}</p>
            <p>{copy.description}</p>
            <p>{copy.intro}</p>
            <p>
              {copy.links.map((link, i) => (
                <span key={link.href}>
                  {i > 0 ? ' · ' : null}
                  <a href={link.href}>{link.text}</a>
                </span>
              ))}
            </p>
          </div>
        </noscript>
        {/* No <ScrollRestoration/>: App's ScrollToTop owns scrolling, as before W4. */}
        <Scripts />
      </body>
    </html>
  );
}

/** Everything main.tsx put around `<App/>`: the providers and the persisted-cache restore. */
export default function Root() {
  // The browser's singleton; on the server a fresh inert client per render.
  const [client] = useState(documentApolloClient);
  // The server's render time, so the clock reads the same while hydrating (@/hooks/useRenderNow).
  const loaded = useRouteLoaderData('root') as DocumentData | undefined;
  return (
    <RenderTimeContext.Provider value={loaded?.renderedAt ?? null}>
      <AppProviders client={client}>
        <PersistedCacheRestore />
        <Outlet />
      </AppProviders>
    </RenderTimeContext.Provider>
  );
}

/**
 * The document when the loader failed (backend down → 503, a bug → 500) or a
 * render threw: its own `<title>` and `noindex` in JSX (React puts them in
 * the head), never the page's canonical.
 */
export function ErrorBoundary() {
  const error = useRouteError();
  const status = isRouteErrorResponse(error) ? error.status : 500;
  const t = tFor(DEFAULT_LOCALE);
  const [heading, text] =
    status === 404
      ? [t('common:document.error.notFound'), '']
      : status === 503
        ? [t('common:document.error.unavailable'), t('common:document.error.unavailableText')]
        : [t('common:document.error.failed'), t('common:document.error.failedText')];
  return (
    <main className="mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center gap-4 px-6 text-center">
      <title>{`${heading} | ${SITE_NAME}`}</title>
      <meta name="robots" content="noindex" />
      <h1 className="text-2xl font-bold text-ink">{heading}</h1>
      {text ? <p className="text-sm text-ink-secondary">{text}</p> : null}
      <a className="font-semibold text-primary-strong underline" href="/">
        {t('common:document.error.home')}
      </a>
    </main>
  );
}
